import { NewAuditLogsObject } from './reportDocuments/attachments/auditLog';

import {
  AttachmentList,
  AttachmentObject,
} from '../../../interfaces/AttachmentObject';
import {
  ReportActionField,
  ReportActionFieldFromAPI,
  ReportActionFieldValueForPatchAPI,
  ReportActionFieldValueFromAPI,
  ReportActionFieldValueObject,
} from '../../../interfaces/reportActions';
import {
  AdditionalInformationOriginalProps,
  AuditLogsFromReportDetailsOriginalProps,
  InternalCommentFromReportDetailsOriginalProps,
  ResponseFiles,
  LinkedReportOptionOriginalProps,
  LinkedReportOption,
} from '../../../interfaces/reportData';

export const transformFiles = (
  responseFiles: ResponseFiles[],
): AttachmentList[] => {
  const transformedFiles = responseFiles.map(
    ({ files, uploader_id, uploader_name }) => {
      const fileList: AttachmentObject[] = files.map((file) => ({
        id: file.id,
        name: file.original_file_name,
        fileType: file.file_type,
        url: file.url,
        attachmentType: file.attachment_type,
        userType: file.user_type,
      }));
      return {
        files: fileList,
        uploaderID: uploader_id,
        uploaderName: uploader_name,
      };
    },
  );
  return transformedFiles;
};

export const transformFilesFromReportInfo = (reportInfo: ResponseFiles[]) => {
  let attachments: Array<AttachmentList> = [];
  attachments = [...attachments, ...transformFiles(reportInfo)];
  return attachments;
};

export const convertIfReadableDate = (value: string): string => {
  // There is a weird JavaScript date bug that returns a valid date when the date string is "Option 1" or "Word 1"
  const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
  if (!isoRegex.test(value)) return value;
  const parsedDate = new Date(value);
  if (isNaN(parsedDate.getTime())) return value;
  return parsedDate.toLocaleString('default', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const transformAuditLogs = (
  logs: Array<AuditLogsFromReportDetailsOriginalProps>,
) => {
  return logs.map(
    ({
      author,
      activity,
      created_at,
    }: AuditLogsFromReportDetailsOriginalProps) => {
      return {
        name: author?.full_name || '',
        activity,
        createdAt: created_at,
      };
    },
  );
};

export const transformInternalComments = (
  logs: Array<InternalCommentFromReportDetailsOriginalProps>,
) => {
  return logs.map(
    ({
      id,
      author,
      content,
      created_at,
      category,
    }: InternalCommentFromReportDetailsOriginalProps) => {
      return {
        id,
        name: author?.full_name || '',
        content,
        createdAt: created_at,
        category: category?.id ? category : null,
      };
    },
  );
};

export const transformAdditionalInformation = (
  data: Array<AdditionalInformationOriginalProps>,
): Array<{ descriptionText: string }> => {
  return data?.map(({ description_text }) => ({
    descriptionText: description_text,
  }));
};
export const transformReportActionFieldsValues = (
  data: Array<ReportActionFieldValueFromAPI>,
): Array<ReportActionFieldValueObject> => {
  return data?.map(({ field_id, field_title, id, value }) => ({
    fieldId: field_id,
    fieldTitle: field_title,
    id,
    value,
  }));
};

export const transformLinkedReports = (
  data: Array<LinkedReportOptionOriginalProps>,
): Array<LinkedReportOption> => {
  return data?.map(({ reference_id, formatted_report_number }) => ({
    referenceId: reference_id,
    formattedReportNumber: formatted_report_number,
  }));
};

export const getTimeColor = (
  dueDate: string,
  isCaseDoNotPurgeEnabled: boolean,
  isCasePrivilegedEnabled: boolean,
) => {
  const transformDate = new Date(dueDate);
  const timeRemaining = transformDate.getTime() - Date.now();
  const hoursRemaining = timeRemaining / (1000 * 60 * 60);

  if (hoursRemaining < 24) {
    return '#E51D1D';
  } else if (hoursRemaining < 48) {
    return '#F49D0C';
  } else if (isCaseDoNotPurgeEnabled || isCasePrivilegedEnabled) {
    return '#FFFFFF';
  }
  return 'inherit';
};
export const getTimeBgColor = (
  dueDate: string,
  isCaseDoNotPurgeEnabled: boolean,
  isCasePrivilegedEnabled: boolean,
) => {
  const transformDate = new Date(dueDate);
  const timeRemaining = transformDate.getTime() - Date.now();
  const hoursRemaining = timeRemaining / (1000 * 60 * 60);

  if (
    (isCaseDoNotPurgeEnabled || isCasePrivilegedEnabled) &&
    hoursRemaining < 48
  ) {
    return '#FFFFFF';
  }
  return 'transparent';
};

export const dateToString = (date: string, isForAuditLogs?: boolean) => {
  if (!isNaN(new Date(date).getDate())) {
    return new Date(date).toLocaleString([], {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
  if (isForAuditLogs) return '--';
  return 'No Due Date';
};

export const timeToString = (date: string) => {
  if (!isNaN(new Date(date).getDate())) {
    return new Date(date).toLocaleString([], {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });
  } else return '--';
};

export const filterAuditLogs = (
  array: NewAuditLogsObject[],
): NewAuditLogsObject[] => {
  return array.filter(
    (item, index, self) =>
      index ===
      self.findIndex(
        (log) =>
          log.activity === item.activity &&
          log.author.full_name === item.author.full_name &&
          dateToString(log.created_at) === dateToString(item.created_at) &&
          timeToString(log.created_at) === timeToString(item.created_at),
      ),
  );
};

export const transformReportActionFieldsOrderFromAPI = (
  apiData: ReportActionFieldFromAPI[],
): ReportActionField[] => {
  if (!apiData) return [];
  const newArrayData: ReportActionField[] = apiData.map((item) => ({
    id: item.id,
    order: item.order,
    title: item.title,
    isShown: item.is_display_on_report_actions_enabled,
    choices: [
      ...(item.pre_defined_dropdown_choices &&
        item.pre_defined_dropdown_choices),
      ...(item.dropdown_choices && item.dropdown_choices),
    ],
    isMandatory: item.is_mandatory,
    isDefault: item.is_default,
    fieldType: item.type,
  }));
  return newArrayData;
};

export const getFieldValue = (
  fieldValues: ReportActionFieldValueObject[],
  title: string,
  fid: number,
): ReportActionFieldValueObject => {
  return (
    fieldValues.find((item) => item.fieldId === fid) || {
      fieldId: fid,
      fieldTitle: title,
      value: '',
    }
  );
};

export const setFieldValue = (
  currfieldValues: ReportActionFieldValueObject[],
  title: string,
  fid: number,
  value: string,
): ReportActionFieldValueObject[] => {
  if (currfieldValues.find((item) => item.fieldId === fid))
    return currfieldValues.map((item) =>
      item.fieldId === fid ? { ...item, value } : item,
    );
  else return [...currfieldValues, { fieldId: fid, fieldTitle: title, value }];
};

export const transformFieldValueForAPI = (
  currfieldValues: ReportActionFieldValueObject[],
  fieldIds: number[],
): ReportActionFieldValueForPatchAPI[] => {
  return currfieldValues
    .filter(
      (item) => item.id && item.fieldId && fieldIds.includes(item.fieldId),
    )
    .map((item) => ({
      id: item.id,
      value: item.value,
    }));
};

export const transformNewFieldValueForAPI = (
  currfieldValues: ReportActionFieldValueObject[],
): ReportActionFieldValueForPatchAPI[] => {
  return currfieldValues
    .filter((item) => !item.id)
    .map((item) => ({
      tenant_case_manager_field: item.fieldId,
      value: item.value,
    }));
};
